/**
 * interior_rooms.ts — THE ROOM PLAN both sides keep, THE PRESENCE RULE both sides apply and THE COARSE STAMP the
 * server's walking grid takes (the interiors programme, slice G "shared rooms", 2026-10-01). PURE: no three, no DOM,
 * no rows. MIRRORED on the server (`server/src/lib/interior/interior_rooms.ts`, written by
 * `tools/sync-interior-mirrors.mjs` — never edit the mirror; one import is rewritten there).
 *
 * THE DEED FRAME LAW. From THE ROOMS REVISION (layout 2) on, a deed's room is planned in the box of the shared
 * FOOTPRINT TABLE (`STRUCTURE_FOOTPRINT`, the row both sides already hold), never in the model's measured box: the
 * client measures the cooked model (9.8195 m), the server reads the table (9.82 m), and a planner fed two numbers a
 * millimetre apart can choose two different rooms. One input, one room — and the DIGEST says so: where a pin row
 * carries the digest the owner's client stamped, the server's plan is the client's exactly when the two are equal.
 * Below layout 2 the client plans as it always did (the measured box — nothing pinned or legacy moves) and the
 * server's plan of such a room is used only for what does not depend on a centimetre: ONE room a floor, the floors,
 * the stairs.
 *
 * THE PRESENCE RULE (`judgePresence`). The server cannot see a walk indoors — its position is 2D and advisory — so
 * presence is BOUNDED TRUST: a report must name a room the plan has; a room change must follow doorways of the room
 * graph, a floor change the stair (or lift) links, and neither faster than a sprint over the distance. Any honest
 * walk is accepted; a teleport is not, until the time a walk would have taken has passed.
 *
 * THE COARSE STAMP (`coarseStamps`). The server's walking grid is 1 m: a 0.4 m partition and a 1.2 m doorway mean
 * nothing on it. A wall is stamped as a band a 4-connected walk cannot cross, a doorway is widened to the DOOR'S
 * SCALE (a versona's body is 0.9 m), and the result is PROVEN before it is handed over: from the building's door
 * every ground-floor room must be reached on the very cells the grid will hold — else the furniture goes, and if
 * that is not enough the partitions go (today's open shell). Nothing is ever sealed.
 */
import { FURNISH_PACKS, footprintOf } from '@shared/types/structures.js';
import { FLOOR_H, interiorSpec } from './building_interiors.js';
import { packView, type PlanInput } from './interior_planner.js';
import { COVER_SIGHT_M, ROOMS_LAYOUT_REVISION, STOREYS_LAYOUT_REVISION, partitionBoxes, type FloorLink, type InteriorDescriptor, type Rect } from './interior_descriptor.js';
import type { InteriorPurpose } from './interior_purpose.js';
import type { Tradition } from './interior_archetypes.js';

/** The rooms flag (server `platform_flag`, ABSENT = ON): off → the walking grid is the shell with a door, presence
 *  reports are accepted and ignored, venues use the box test. */
export const ROOMS_FLAG = 'interior_rooms_v1';
/** The layout a new furnishing takes from this release on (the client's `CURRENT_LAYOUT_REVISION`, the server's
 *  `LAYOUT_REVISION_CURRENT`) — THE STOREYS REVISION since slice U (2026-10-02; the rooms revision before it). A pin
 *  at the rooms revision or later is checked digest for digest (`interior_room_ops` reads `ROOMS_LAYOUT_REVISION`). */
export const ROOMS_LAYOUT_CURRENT = STOREYS_LAYOUT_REVISION;
/** THE STOREYS FLAG (server `platform_flag`, ABSENT = ON; slice U): off → a new furnishing and a Refit are stamped at
 *  THE ROOMS REVISION (layout 2) and a building with no pin (the guild hall) is planned there; a pin already written
 *  at layout 3 keeps it (a pinned room never moves by a flag). */
export const STOREYS_FLAG = 'interior_storeys_v1';

// ─── The deed frame ──────────────────────────────────────────────────────────────────────────────────────────────────

/** The box a deed's room is planned in from layout 2 on: the footprint table's (w, h, d). */
export function deedPlanBox(type: string): { w: number; h: number; d: number } {
  const f = footprintOf(type);
  return { w: 2 * f.hx, h: f.h, d: 2 * f.hz };
}
/** How far the table may stand from the model the client measured before the client plans in its own box instead
 *  (and says so: its digest will not be the server's). The table is the cooked model rounded to a centimetre. */
export const DEED_BOX_TOLERANCE_M = 0.08;
const floorsOf = (h: number): number => Math.max(1, Math.min(8, Math.floor((h - 1.6) / FLOOR_H)));
export function deedBoxAgrees(type: string, measured: { w: number; h: number; d: number }): boolean {
  const t = deedPlanBox(type);
  return Math.abs(t.w - measured.w) <= DEED_BOX_TOLERANCE_M && Math.abs(t.d - measured.d) <= DEED_BOX_TOLERANCE_M && floorsOf(t.h) === floorsOf(measured.h);
}
/** The planner's input for a deed the way the SERVER builds it (and the client from layout 2 on). `tradition` only
 *  at layout ≥ 1 (a legacy room takes none). */
export function deedPlanInput(structureId: string | bigint | number, type: string, purpose: InteriorPurpose, layout: number, tradition?: Tradition): PlanInput {
  const input: PlanInput = {
    buildingId: `deed:${structureId}`, key: `deed:${type}`, ...deedPlanBox(type), purpose,
    algorithm: layout <= 0 ? 'legacy' : 'planner', options: { layoutRevision: Math.max(0, layout) },
  };
  if (tradition && layout >= 1) input.tradition = tradition;
  return input;
}
/** The traditions a pinned digest is looked for under (the planner's default first — what an unstamped pin plans with). */
export const ROOM_TRADITIONS: ReadonlyArray<Tradition | undefined> = [undefined, 'neutral', 'aurelian', 'borean'];

// ─── The compact room plan ───────────────────────────────────────────────────────────────────────────────────────────

export interface PlanBox { x0: number; z0: number; x1: number; z1: number }
export interface RoomPlanRoom { id: string; zone: string; unitId: string; access: string; rects: Rect[]; x: number; z: number }
export interface RoomPlanDoor { id: string; a: string; b: string; x: number; z: number; width: number; axis: 'x' | 'z' }
export interface RoomPlanNode { id: string; x: number; z: number; room: string }
export interface RoomPlanFloor {
  floor: number;
  rooms: RoomPlanRoom[];
  doorways: RoomPlanDoor[];
  /** The vertical links' ends on this floor (`stair-foot`, `landing`, `lift-call`) and the room each stands in. */
  nodes: RoomPlanNode[];
  /** The partitions' solid boxes (the walls less their doorways), building-local metres; `low` = a screen. */
  walls: Array<PlanBox & { low: boolean }>;
  /** The walk-blocking pieces a visitor collides with (the pack's view), as boxes — with what a BLOW needs to know
   *  (slice K, lib/indoor_combat.ts): `h` the cover height (m; 0 for a detail piece — a render filter a low tier may
   *  not draw — and for a tall see-through one: a plant, a lamp, a rack), `sight` = it blocks a line of sight. */
  pieces: Array<PlanBox & { h: number; sight: boolean }>;
}
export interface RoomPlan {
  digest: string; layout: number;
  /** The interior's half extents (the shell's core) and its floors. */
  hw: number; hd: number;
  floors: RoomPlanFloor[];
  links: FloorLink[];
  /** (slice K) The lift shaft's rect when a lift joins the floors (slice L: solid on every storey), else null. */
  shaft?: PlanBox | null;
  /** Estimated bytes (the memo's budget). */
  bytes: number;
}

/** A point's room on a floor of the plan (the nearest within `slackM` when it stands in a wall). */
export function planRoomAt(f: RoomPlanFloor, x: number, z: number, slackM = 0.6): string {
  let best = '', bestD = Infinity;
  for (const room of f.rooms) for (const q of room.rects) {
    const dx = Math.max(q.x0 - x, 0, x - q.x1), dz = Math.max(q.z0 - z, 0, z - q.z1);
    if (dx === 0 && dz === 0) return room.id;
    const d2 = dx * dx + dz * dz;
    if (d2 < bestD) { bestD = d2; best = room.id; }
  }
  return bestD <= slackM * slackM ? best : '';
}

/** `pack`: 1..5 = that furnish pack's view; 0 = an unfurnished pack interior (no piece); 'all' = every planned piece
 *  (a building that furnishes itself — the guild hall, a venue). */
export function roomPlanFrom(d: InteriorDescriptor, input: Pick<PlanInput, 'w' | 'h' | 'd' | 'key'>, pack: number | 'all'): RoomPlan {
  const spec = interiorSpec(input.w, input.h, input.d, input.key);
  const seated = new Set<string>();
  if (pack === 'all') for (const f of d.floors) for (const p of f.pieces) seated.add(`${f.floor}|${p.slot}`);
  else {
    const fp = FURNISH_PACKS.find((q) => q.pack === pack);
    if (fp) for (const fl of packView(d, fp)) for (const pp of fl) seated.add(`${pp.piece.floor}|${pp.piece.slot}`);
  }
  let bytes = 512;
  const floors: RoomPlanFloor[] = [...d.floors].sort((a, b) => a.floor - b.floor).map((f) => {
    const rooms: RoomPlanRoom[] = f.rooms.map((r) => ({ id: r.id, zone: r.zone, unitId: r.unitId ?? '', access: r.access, rects: r.rects, x: r.x, z: r.z }));
    const fl: RoomPlanFloor = {
      floor: f.floor, rooms,
      doorways: f.doorways.map((q) => ({ id: q.id, a: q.a, b: q.b, x: q.x, z: q.z, width: q.width, axis: q.axis })),
      nodes: [], walls: [], pieces: [],
    };
    for (const n of f.routeNodes) if (n.id === 'stair-foot' || n.id === 'landing' || n.id === 'lift-call') fl.nodes.push({ id: n.id, x: n.x, z: n.z, room: planRoomAt(fl, n.x, n.z, 1.5) || rooms[0]?.id || '' });
    for (const w of f.partitions) for (const q of partitionBoxes(w)) fl.walls.push({ ...q, low: w.height < 2 });
    for (const p of f.pieces) {
      if (!p.blocksWalk || !seated.has(`${f.floor}|${p.slot}`)) continue;
      const c = Math.abs(Math.cos(p.yaw)), s = Math.abs(Math.sin(p.yaw));
      const hx = p.half.w * c + p.half.d * s, hz = p.half.w * s + p.half.d * c;
      const solid = p.tier !== 'detail' && (p.blocksSight || p.coverHeight < COVER_SIGHT_M); // (slice K) what covers a body
      fl.pieces.push({ x0: p.x - hx, x1: p.x + hx, z0: p.z - hz, z1: p.z + hz, h: solid ? p.coverHeight : 0, sight: p.tier !== 'detail' && p.blocksSight });
    }
    bytes += 256 + rooms.reduce((n, r) => n + 160 + r.rects.length * 48, 0) + fl.doorways.length * 120 + fl.walls.length * 56 + fl.pieces.length * 64;
    return fl;
  });
  const shaft = d.liftCandidate && d.links.some((l) => l.kind === 'lift') ? { ...d.liftCandidate.rect } : null; // (slice K) what a line of sight stops on
  return { digest: d.digest, layout: d.recipe.layoutRevision, hw: spec.hw, hd: spec.hd, floors, links: d.links, shaft, bytes: bytes + d.links.length * 96 };
}
/** The same building with ONE room a floor (its floors, stairs and lifts kept): what the server judges by when the
 *  reporter's plan is not the server's (their digest differs) — floors are the shell's, rooms were the plan's. */
export function floorsOnly(plan: RoomPlan): RoomPlan {
  return {
    ...plan,
    floors: plan.floors.map((f) => {
      const id = `room/f${f.floor}/main`;
      return {
        ...f, doorways: [], walls: [], pieces: [],
        rooms: [{ id, zone: 'main', unitId: '', access: 'public', rects: [{ x0: -plan.hw, z0: -plan.hd, x1: plan.hw, z1: plan.hd }], x: f.rooms[0]?.x ?? 0, z: f.rooms[0]?.z ?? 0 }],
        nodes: f.nodes.map((n) => ({ ...n, room: id })),
      };
    }),
  };
}
/** The unit a room of the plan is ('' = none). */
export function planUnitOfRoom(plan: RoomPlan, roomId: string): string {
  for (const f of plan.floors) for (const r of f.rooms) if (r.id === roomId) return r.unitId;
  return '';
}
export function planHasRoom(plan: RoomPlan, floor: number, roomId: string): boolean {
  return !!plan.floors[floor]?.rooms.some((r) => r.id === roomId);
}
/** Does the plan say anything a centimetre could change? (more than one room on some floor) */
export function planHasRooms(plan: RoomPlan): boolean {
  return plan.floors.some((f) => f.rooms.length > 1);
}

// ─── THE TENANT'S DOOR (slice G task 2, 2026-10-02) ──────────────────────────────────────────────────────────────

/** What the door-safety rule reads of a floor (a `RoomPlanFloor` and a descriptor's floor both give it). */
export interface DoorSafeFloor {
  rooms: ReadonlyArray<{ id: string; unitId?: string }>;
  doorways: ReadonlyArray<{ a: string; b: string }>;
  /** The rooms the floor's vertical links stand in (the stair's foot and landing, the lift's call point). */
  nodeRooms: ReadonlyArray<string>;
}
/**
 * MAY A LET UNIT'S DOOR BE SHUT ON THIS FLOOR? Only when shutting EVERY unit's door seals nobody in and cuts nothing
 * off: (a) every room that is not a unit is reached from the floor's entry room (`rooms[0]`) without crossing a unit;
 * (b) every unit room has a doorway onto such a room; (c) no stair or lift stands in a unit. A floor that fails is
 * never doored — the server refuses nothing there and the visit hangs no leaf. Every room the planner lays today
 * passes (`verify:interior-rooms` sweeps the deed set); the rule is the net under a future program.
 */
export function floorDoorSafe(f: DoorSafeFloor): boolean {
  const unit = new Set<string>();
  for (const r of f.rooms) if (r.unitId) unit.add(r.id);
  if (unit.size === 0) return true;
  const entry = f.rooms[0]?.id ?? '';
  if (entry === '' || unit.has(entry)) return false;
  for (const n of f.nodeRooms) if (unit.has(n)) return false;
  const seen = new Set<string>([entry]), fronted = new Set<string>();
  const open: string[] = [entry];
  while (open.length > 0) {
    const at = open.pop() as string;
    for (const q of f.doorways) {
      const to = q.a === at ? q.b : q.b === at ? q.a : '';
      if (to === '') continue;
      if (unit.has(to)) { fronted.add(to); continue; }
      if (!seen.has(to)) { seen.add(to); open.push(to); }
    }
  }
  for (const r of f.rooms) if (unit.has(r.id) ? !fronted.has(r.id) : !seen.has(r.id)) return false;
  return true;
}
/** The unit a room of the plan IS and whether its floor may be doored ('' / false: no such room, or not a unit). */
export function planDoorOf(plan: RoomPlan, roomId: string): { unitId: string; safe: boolean } {
  for (const f of plan.floors) {
    for (const r of f.rooms) {
      if (r.id !== roomId) continue;
      if (r.unitId === '') return { unitId: '', safe: false };
      return { unitId: r.unitId, safe: floorDoorSafe({ rooms: f.rooms, doorways: f.doorways, nodeRooms: f.nodes.map((n) => n.room) }) };
    }
  }
  return { unitId: '', safe: false };
}

// ─── The presence rule ───────────────────────────────────────────────────────────────────────────────────────────────

/** The fastest a body crosses a room (the sprint; a dash is a 250 ms burst) and rides a lift (m/s). */
export const PRESENCE_RUN_MPS = 9;
export const PRESENCE_LIFT_MPS = 4;
/** Slack on every time bound (s): the report is debounced, the clock steps. 0.3 since 2026-10-02 (it was 0.75, which
 *  left a single storey's stair run — ~0.85 s at a sprint — refusable only within 0.1 s of the last report). A body
 *  that really sprints the stairs reports ≥ 0.8 s after its last row (the run itself; both reports carry the same
 *  350 ms debounce), so 0.3 still covers the reducer's arrival jitter; a refusal costs nothing and is retried. */
export const PRESENCE_SLACK_S = 0.3;
/** The room a visit begins in: the ground floor's entry. */
export const entryRoomOf = (plan: RoomPlan): string => plan.floors[0]?.rooms[0]?.id ?? '';

export interface PresenceAt { floor: number; roomId: string; atS: number }
export type PresenceVerdict = { ok: true; costM: number; needS: number } | { ok: false; why: string; costM?: number; needS?: number };

/** Walking metres between two rooms of a floor through the doorways (0 when they are one room or neighbours — the
 *  body may be standing in the doorway), or −1 when no doorway chain joins them. */
export function roomPathM(f: RoomPlanFloor, from: string, to: string): number {
  if (from === to) return 0;
  // Dijkstra over doorways: a state is "standing in doorway i"; the first doorway costs nothing
  const doors = f.doorways;
  const dist = new Array<number>(doors.length).fill(Infinity);
  const done = new Array<boolean>(doors.length).fill(false);
  doors.forEach((q, i) => { if (q.a === from || q.b === from) dist[i] = 0; });
  let best = Infinity;
  for (;;) {
    let k = -1;
    for (let i = 0; i < doors.length; i++) if (!done[i] && dist[i] < Infinity && (k < 0 || dist[i] < dist[k])) k = i;
    if (k < 0) break;
    done[k] = true;
    const q = doors[k];
    if (q.a === to || q.b === to) { best = Math.min(best, dist[k]); continue; }
    for (let i = 0; i < doors.length; i++) {
      if (done[i]) continue;
      const o = doors[i];
      // the two doorways share a room (the one walked through)
      const shared = (o.a === q.a || o.a === q.b || o.b === q.a || o.b === q.b);
      if (!shared) continue;
      const nd = dist[k] + Math.hypot(o.x - q.x, o.z - q.z);
      if (nd < dist[i]) dist[i] = nd;
    }
  }
  return best === Infinity ? -1 : best;
}

/** THE PRESENCE RULE. `prev` = the caller's standing row for THIS building (null = they are entering it). */
export function judgePresence(plan: RoomPlan, prev: PresenceAt | null, floor: number, roomId: string, nowS: number): PresenceVerdict {
  const fl = plan.floors[floor];
  if (!fl || !Number.isInteger(floor)) return { ok: false, why: 'no such floor' };
  if (!fl.rooms.some((r) => r.id === roomId)) return { ok: false, why: 'no such room' };
  if (!prev) {
    // a visit begins at the door: the ground floor's entry room
    if (floor !== 0 || roomId !== entryRoomOf(plan)) return { ok: false, why: 'a visit begins at the door' };
    return { ok: true, costM: 0, needS: 0 };
  }
  const elapsed = Math.max(0, nowS - prev.atS);
  let costM = 0, needS = 0;
  if (prev.floor === floor) {
    const m = roomPathM(fl, prev.roomId, roomId);
    if (m < 0) return { ok: false, why: 'no doorway joins those rooms' };
    costM = m; needS = m / PRESENCE_RUN_MPS;
  } else {
    // floor by floor along the links: the stairs (walked) or the lift (ridden), whichever is quicker on each step
    const dir = floor > prev.floor ? 1 : -1;
    let room = prev.roomId;
    for (let f = prev.floor; f !== floor; f += dir) {
      const lo = Math.min(f, f + dir);
      const here = plan.floors[f], next = plan.floors[f + dir];
      if (!here || !next) return { ok: false, why: 'no such floor' };
      let bestS = Infinity, bestM = 0, bestRoom = '';
      for (const l of plan.links) {
        if (l.from.floor !== lo || l.to.floor !== lo + 1) continue;
        const dep = dir > 0 ? l.from.node : l.to.node, arr = dir > 0 ? l.to.node : l.from.node;
        const dn = here.nodes.find((n) => n.id === dep), an = next.nodes.find((n) => n.id === arr);
        if (!dn || !an) continue;
        const toDep = roomPathM(here, room, dn.room);
        if (toDep < 0) continue;
        const s = toDep / PRESENCE_RUN_MPS + l.costM / (l.kind === 'lift' ? PRESENCE_LIFT_MPS : PRESENCE_RUN_MPS);
        if (s < bestS) { bestS = s; bestM = toDep + l.costM; bestRoom = an.room; }
      }
      if (bestS === Infinity) return { ok: false, why: 'no stair or lift joins those floors' };
      needS += bestS; costM += bestM; room = bestRoom;
    }
    const m = roomPathM(fl, room, roomId);
    if (m < 0) return { ok: false, why: 'no doorway joins those rooms' };
    costM += m; needS += m / PRESENCE_RUN_MPS;
  }
  if (elapsed + PRESENCE_SLACK_S < needS) return { ok: false, why: 'faster than a walk', costM, needS };
  return { ok: true, costM, needS };
}

// ─── The coarse stamp (the server's 1 m walking grid) ────────────────────────────────────────────────────────────────

/** A walk-in box's wall band and doorway on the grid — MIRRORS of structure_footprints.ts `SHELL_WALL_M` /
 *  `SHELL_DOOR_M` (the harness pins them): the simulation below must read the cells the grid will. */
export const COARSE_SHELL_WALL_M = 1.2;
export const COARSE_SHELL_DOOR_M = 3.0;
/** A partition's band on the grid (m): ≥ 1 cell at any yaw. A doorway's gap (m): two cells at any yaw. */
export const COARSE_WALL_M = 1.2;
export const COARSE_DOOR_M = 3.0;
/** A piece is stamped only when it is at least this wide BOTH ways (m): a chair is not a wall on a 1 m grid. */
export const COARSE_PIECE_MIN_M = 1.4;

/** A solid box in the STRUCTURE's own frame (metres from its row position, before its yaw): centre + half extents. */
export interface CoarseBox { lx: number; lz: number; hw: number; hd: number }
export interface CoarseResult {
  boxes: CoarseBox[];
  /** What was given up so that nothing is sealed: nothing, the furniture, or the partitions too (the open shell). */
  dropped: 'none' | 'pieces' | 'all';
  rooms: number; reached: number;
}

/**
 * The boxes to stamp inside a walk-in deed's shell, PROVEN on the grid they will be stamped into.
 *   fp      the footprint's half extents (the shell the grid already stamps: walls COARSE_SHELL_WALL_M, the doorway on −Z)
 *   pose    the structure row's position and yaw
 *   grid    the target grid's phase: a cell centre is `ox + (i + 0.5) · cell`
 */
export function coarseStamps(plan: RoomPlan, fp: { hx: number; hz: number }, pose: { x: number; z: number; yaw: number }, grid: { ox: number; oz: number; cell: number }): CoarseResult {
  const f0 = plan.floors[0];
  const none: CoarseResult = { boxes: [], dropped: 'all', rooms: f0?.rooms.length ?? 0, reached: 0 };
  if (!f0) return none;
  const closing = f0.walls.filter((w) => !w.low);
  if (closing.length === 0 && f0.pieces.length === 0) return { boxes: [], dropped: 'none', rooms: f0.rooms.length, reached: f0.rooms.length };
  // wall bands: each solid span of a partition, widened across to COARSE_WALL_M; a doorway's gap widened along to COARSE_DOOR_M
  const bands: CoarseBox[] = [];
  for (const w of closing) {
    const alongX = w.x1 - w.x0 >= w.z1 - w.z0;
    bands.push(alongX
      ? { lx: (w.x0 + w.x1) / 2, lz: (w.z0 + w.z1) / 2, hw: (w.x1 - w.x0) / 2, hd: COARSE_WALL_M / 2 }
      : { lx: (w.x0 + w.x1) / 2, lz: (w.z0 + w.z1) / 2, hw: COARSE_WALL_M / 2, hd: (w.z1 - w.z0) / 2 });
  }
  const gaps: CoarseBox[] = f0.doorways.map((q) => (q.axis === 'x'
    ? { lx: q.x, lz: q.z, hw: Math.max(q.width, COARSE_DOOR_M) / 2, hd: COARSE_WALL_M / 2 + 0.05 }
    : { lx: q.x, lz: q.z, hw: COARSE_WALL_M / 2 + 0.05, hd: Math.max(q.width, COARSE_DOOR_M) / 2 }));
  // a band less its gaps → the boxes actually stamped
  const cut = (b: CoarseBox): CoarseBox[] => {
    let parts: CoarseBox[] = [b];
    const alongX = b.hw >= b.hd;
    for (const g of gaps) {
      const next: CoarseBox[] = [];
      for (const p of parts) {
        const hit = Math.abs(g.lx - p.lx) < g.hw + p.hw && Math.abs(g.lz - p.lz) < g.hd + p.hd;
        if (!hit) { next.push(p); continue; }
        if (alongX) {
          const a0 = p.lx - p.hw, a1 = p.lx + p.hw, g0 = g.lx - g.hw, g1 = g.lx + g.hw;
          if (g0 - a0 > 0.05) next.push({ ...p, lx: (a0 + g0) / 2, hw: (g0 - a0) / 2 });
          if (a1 - g1 > 0.05) next.push({ ...p, lx: (g1 + a1) / 2, hw: (a1 - g1) / 2 });
        } else {
          const a0 = p.lz - p.hd, a1 = p.lz + p.hd, g0 = g.lz - g.hd, g1 = g.lz + g.hd;
          if (g0 - a0 > 0.05) next.push({ ...p, lz: (a0 + g0) / 2, hd: (g0 - a0) / 2 });
          if (a1 - g1 > 0.05) next.push({ ...p, lz: (g1 + a1) / 2, hd: (a1 - g1) / 2 });
        }
      }
      parts = next;
    }
    return parts;
  };
  const wallBoxes = bands.flatMap(cut);
  const pieceBoxes: CoarseBox[] = f0.pieces
    .filter((p) => p.x1 - p.x0 >= COARSE_PIECE_MIN_M && p.z1 - p.z0 >= COARSE_PIECE_MIN_M)
    .map((p) => ({ lx: (p.x0 + p.x1) / 2, lz: (p.z0 + p.z1) / 2, hw: (p.x1 - p.x0) / 2, hd: (p.z1 - p.z0) / 2 }));

  // THE PROOF: the footprint's cells as the grid will hold them (centre-in-box, the grid's own rule)
  const c = Math.cos(pose.yaw), s = Math.sin(pose.yaw);
  const reach = Math.hypot(fp.hx, fp.hz) + grid.cell;
  const i0 = Math.floor((pose.x - reach - grid.ox) / grid.cell), i1 = Math.floor((pose.x + reach - grid.ox) / grid.cell);
  const j0 = Math.floor((pose.z - reach - grid.oz) / grid.cell), j1 = Math.floor((pose.z + reach - grid.oz) / grid.cell);
  const nx = i1 - i0 + 1, nz = j1 - j0 + 1;
  const local = new Float64Array(nx * nz * 2);
  const kind = new Uint8Array(nx * nz); // 0 outside the building · 1 shell wall · 2 the doorway · 3 inside
  for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
    const wx = grid.ox + (i0 + i + 0.5) * grid.cell, wz = grid.oz + (j0 + j + 0.5) * grid.cell;
    const rx = wx - pose.x, rz = wz - pose.z;
    const qx = rx * c - rz * s, qz = rx * s + rz * c; // structure_footprints' un-rotation
    const k = j * nx + i;
    local[2 * k] = qx; local[2 * k + 1] = qz;
    if (Math.abs(qx) > fp.hx || Math.abs(qz) > fp.hz) { kind[k] = 0; continue; }
    const inWall = fp.hx - Math.abs(qx) < COARSE_SHELL_WALL_M || fp.hz - Math.abs(qz) < COARSE_SHELL_WALL_M;
    if (!inWall) kind[k] = 3;
    else kind[k] = qz < 0 && fp.hz - Math.abs(qz) < COARSE_SHELL_WALL_M && Math.abs(qx) <= COARSE_SHELL_DOOR_M / 2 && fp.hx - Math.abs(qx) >= COARSE_SHELL_WALL_M ? 2 : 1;
  }
  const inBox = (b: CoarseBox, qx: number, qz: number): boolean => Math.abs(qx - b.lx) <= b.hw && Math.abs(qz - b.lz) <= b.hd;
  const prove = (boxes: CoarseBox[]): number => {
    const solid = new Uint8Array(nx * nz);
    for (let k = 0; k < nx * nz; k++) {
      if (kind[k] === 1) { solid[k] = 1; continue; }
      if (kind[k] !== 3) continue;
      for (const b of boxes) if (inBox(b, local[2 * k], local[2 * k + 1])) { solid[k] = 1; break; }
    }
    // flood from the doorway's cells (and the street before them), 4-connected like the walker
    const seen = new Uint8Array(nx * nz);
    const queue: number[] = [];
    for (let k = 0; k < nx * nz; k++) if (kind[k] === 2 && !solid[k]) { seen[k] = 1; queue.push(k); }
    for (let h = 0; h < queue.length; h++) {
      const k = queue[h], i = k % nx;
      for (const n of [i + 1 < nx ? k + 1 : -1, i > 0 ? k - 1 : -1, k + nx < nx * nz ? k + nx : -1, k - nx >= 0 ? k - nx : -1]) {
        if (n < 0 || seen[n] || solid[n] || kind[n] === 0) continue;
        seen[n] = 1; queue.push(n);
      }
    }
    // a room is reached when a cell whose centre stands in it was (a room too small to hold a cell centre has no say)
    let reached = 0;
    for (const room of f0.rooms) {
      let any = false, hit = false;
      for (let k = 0; k < nx * nz && !hit; k++) {
        if (kind[k] !== 3) continue;
        const qx = local[2 * k], qz = local[2 * k + 1];
        if (!room.rects.some((q) => qx >= q.x0 && qx <= q.x1 && qz >= q.z0 && qz <= q.z1)) continue;
        any = true;
        if (seen[k]) hit = true;
      }
      if (hit || !any) reached++;
    }
    return reached;
  };
  const rooms = f0.rooms.length;
  const full = [...wallBoxes, ...pieceBoxes];
  let reached = prove(full);
  if (reached === rooms) return { boxes: full, dropped: 'none', rooms, reached };
  reached = prove(wallBoxes);
  if (reached === rooms) return { boxes: wallBoxes, dropped: 'pieces', rooms, reached };
  return { boxes: [], dropped: 'all', rooms, reached };
}
